import { Tooltip } from '@material-ui/core';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { toastError } from '@components/Toast';

import ApiClient from '@lib/apiClient';

import {
	capitalizeFirstLetter,
	changeDateFormat,
	convertNumberToUSFormat,
	generateRandomString,
	getShortName,
	onErrorImageLoad,
	repeatTag,
	toOrdinalSuffix
} from '@utils/Util';

import { APPROVAL_TYPE_ETD, APPROVAL_TYPE_FACTORY_PRICE } from '@/constants/constant';

const OrderLists = ({ orderStore = [], isStartFromHere, source, activeTab }) => {
	const navigate = useNavigate();
	const location = useLocation();

	const renderOrderImage = (imageSrc) => {
		if (imageSrc) {
			return (
				<img src={imageSrc} alt="design" onError={(e) => onErrorImageLoad(e, imageSrc, 'product')} />
			);
		} else {
			return <img src={`${process.env.PUBLIC_URL}/images/default_product.svg`} alt="design" />;
		}
	};

	const renderETD = (item) => {
		return item?.deliveryDateList?.map((etd, index) => {
			return (
				<ul className="start" key={`${generateRandomString}-${index}`}>
					<li>{toOrdinalSuffix(index + 1)} ETD:</li>
					<li>{changeDateFormat(etd, 'YYYY-MM-DD', 'DD MMM, YYYY')}</li>
				</ul>
			);
		});
	};

	const onAddMixpanelEvent = async (orderId) => {
		await ApiClient.POST('orderCommercialInvoice', { orderId }).catch(() => {
			toastError('Event was unsuccessful!');
		});
	};

	const handleRoute = (item) => {
		const targetPath = getOrderRoutePath(item);

		if (source === 'CM') {
			onAddMixpanelEvent(item.orderId);
		}

		navigate(targetPath);
	};

	const getOrderRoutePath = (item) => {
		let activeTab = new URLSearchParams(location.search).get('active');

		if (source === 'CM') {
			return '/orders/view/' + item.orderId + '?workTab=CI';
		} else if (source === 'SCPI') {
			let documentType = item.documentType;
			if (documentType === 'PROFORMA_INVOICE') {
				return '/orders/view/' + item.orderId + '?workTab=PI&routeFrom=SCPI';
			} else {
				return '/orders/view/' + item.orderId + '?workTab=Sales contract&routeFrom=SCPI';
			}
		} else {
			if (activeTab === 'ON_SHIPMENT') {
				return '/orders/view/' + item.orderId + '?workTab=CI';
			} else if (activeTab === 'APPROVAL') {
				if (item?.approvalType === APPROVAL_TYPE_ETD) {
					return (
						'/orders/view/' +
						item.orderId +
						`?workTab=Order info&routeFrom=ETD_APPROVAL&requestId=${item.id}`
					);
				}
				if (item?.approvalType === APPROVAL_TYPE_FACTORY_PRICE) {
					return (
						'/orders/view/' +
						item.orderId +
						`?workTab=Supplier&routeFrom=FACTORY_PRICE_APPROVAL&requestId=${item.id}`
					);
				}
				return (
					'/orders/view/' +
					item.orderId +
					`?workTab=Order info&routeFrom=ORDER_CANCEL&requestId=${item.id}`
				);
			} else {
				return '/orders/view/' + item.orderId;
			}
		}
	};

	const renderOrderStatus = (item) => {
		let activeTab = new URLSearchParams(location.search).get('active');
		if (activeTab === 'COMPLETED' || activeTab === 'ON_SHIPMENT') {
			return <span>DELIVERED </span>;
		} else if (Math.sign(item.timeLeft) === -1) {
			return <span className="overdue-time">Overdue&nbsp;</span>;
		} else {
			return <span className="weight-300">ETD </span>;
		}
	};

	const renderDeliveryDate = (item) => {
		let activeTab = new URLSearchParams(location.search).get('active');
		if (activeTab === 'COMPLETED' && item.completionDate) {
			return (
				<span className="completed-days">
					&nbsp;|&nbsp;
					{item.completionDate ? changeDateFormat(item.completionDate, 'YYYY-MM-DD', 'DD-MMM') : ''}
				</span>
			);
		}
	};

	const renderOrderStatusWiseDate = (item) => {
		let activeTab = new URLSearchParams(location.search).get('active');
		if (activeTab === 'COMPLETED' || activeTab === 'ON_SHIPMENT') {
			return (
				<>
					<span className="completed-days">
						{item.actualDeliveryDate
							? changeDateFormat(item.actualDeliveryDate, 'YYYY-MM-DD', 'DD-MMM')
							: '--'}
						<img
							src={process.env.PUBLIC_URL + '/icons/status-completed.svg'}
							className="ml-1"
							alt=""
						/>
					</span>
					{Math.sign(item.deliveryExtraDay) === -1 ? (
						<span className="green">&nbsp;{item.deliveryExtraDay}d</span>
					) : (
						<span className="red">
							{item.deliveryExtraDay ? ` +${item.deliveryExtraDay}d` : ''}
						</span>
					)}
				</>
			);
		} else if (Math.sign(item.timeLeft) === -1) {
			return (
				<span className="overdue-days">
					{Math.abs(item.timeLeft) + ' Days'}
					<img src={process.env.PUBLIC_URL + '/icons/status-overdue.svg'} className="ml-1" alt="" />
				</span>
			);
		} else {
			return (
				<span className="remaining-days">
					{item?.deliveryDateList?.length > 0 &&
						changeDateFormat(item.deliveryDateList[0], 'YYYY-MM-DD', 'DD-MMM')}
					<img src={process.env.PUBLIC_URL + '/icons/status-ongoing.svg'} className="ml-1" alt="" />
				</span>
			);
		}
	};

	const renderDeliveryStatus = (item) => {
		return (
			<div className="delivery-date-status">
				{renderOrderStatus(item)}
				<span className="date-info">
					{renderOrderStatusWiseDate(item)}
					{renderDeliveryDate(item)}
					<div className="etd-dates shadow-2dp">{renderETD(item)}</div>
				</span>
			</div>
		);
	};

	const renderRoleWiseHeading = (item) => {
		if (location.pathname.indexOf('/orders/my-order') !== -1) {
			return (
				<div className="pos">
					<Tooltip title={item?.name} placement={'top'} arrow>
						<span className="regular-14">{getShortName(item?.name, 24)}</span>
					</Tooltip>
					<span className="regular-14 gray_dark_02">&nbsp;({item.orderRefNumber})</span>
				</div>
			);
		} else {
			return (
				<div className="pos">
					<Tooltip title={item?.poNumberList?.join(',')} placement={'top'} arrow>
						<span className="regular-14">{getShortName(item?.poNumberList?.join(','), 24)}</span>
					</Tooltip>
					<span className="regular-14 gray_dark_02">&nbsp;({item.orderRefNumber})</span>
				</div>
			);
		}
	};

	const renderOrderList = () => {
		return orderStore?.map((item, index) => {
			let docs = item?.orderProductList;
			const targetPath = getOrderRoutePath(item);
			return (
				<a
					className={`single-order-card cursor-pointer ${
						isStartFromHere && index === 0 ? 'pending-start' : ''
					}`}
					key={`order_response_${index}`}
					href={targetPath}
					onClick={(event) => {
						event.preventDefault();
						handleRoute(item);
					}}
				>
					<div className="design-images">
						{renderOrderImage(docs?.length > 0 ? docs[0]?.image : null)}
						{renderOrderImage(docs?.length > 1 ? docs[1]?.image : null)}
						{renderOrderImage(docs?.length > 2 ? docs[2]?.image : null)}
						{renderOrderImage(docs?.length > 3 ? docs[3]?.image : null)}
					</div>
					{item?.status === 'PENDING' && item?.isDrafted && (
						<div className="draft-status">
							<span className="status garments rounded">Draft</span>
						</div>
					)}

					<div className="order-details">
						<div className="po-numbers d-flex align-items-center">
							{renderRoleWiseHeading(item)}
							<div className="ml-2">{item.isRepeatOrder && repeatTag()}</div>
						</div>
						<ul className="order-quantity-details d-flex">
							<li>
								{item.totalStyles}&nbsp;
								{item.totalStyles > 1 ? 'Styles' : 'Style'}
							</li>
							<li>{item.orderQuantity} units</li>
							<li>${convertNumberToUSFormat(item.orderValue)}</li>
						</ul>
						{renderDeliveryStatus(item)}
						{item?.status !== 'PENDING' &&
							item?.status !== 'ON_SHIPMENT' &&
							item?.status !== 'COMPLETED' &&
							item?.status !== 'REQUESTED' &&
							item?.status !== 'APPROVED' &&
							item?.requestType !== 'PRODUCT' &&
							item?.requestType !== 'ORDER' &&
							source !== 'SCPI' && (
								<div className="round-progress-68">
									<div
										className="status pending cir-progress"
										data-percentage={item?.percentageOfCompleteness}
									>
										<span className="cir-progress-left">
											<span className="cir-progress-bar" />
										</span>
										<span className="cir-progress-right">
											<span className="cir-progress-bar" />
										</span>
										<div className="cir-progress-value">
											<div className="task-value">
												{item?.percentageOfCompleteness}%
											</div>
										</div>
									</div>
								</div>
							)}

						{(item?.status !== 'REQUESTED' || item?.status !== 'APPROVED') &&
							activeTab === 'APPROVAL' &&
							item?.approvalType === APPROVAL_TYPE_ETD && (
								<div className="order-cancel-status">
									<p className="order-cancel-ETD order-cancel">
										ETD change <span className="devider">|</span>{' '}
										<span className="weight-500 text-uppercase">
											{item?.pendingStyleCount > 1
												? `${item.pendingStyleCount} requests`
												: changeDateFormat(
														item?.requestedAt,
														'YYYY-MM-DD',
														'DD MMM YYYY'
													)}
										</span>
									</p>
									<p className="regular-12 gray_dark_02 text-right">
										By: {item?.requestedBy}
									</p>
								</div>
							)}

						{activeTab === 'APPROVAL' && item?.approvalType === APPROVAL_TYPE_FACTORY_PRICE && (
							<div className="order-cancel-status">
								<p className="order-cancel-ETD order-cancel">
									Margin Approval <span className="devider">|</span>{' '}
									<span className="weight-500 text-uppercase">
										{item?.pendingStyleCount > 1
											? `${item.pendingStyleCount} requests`
											: changeDateFormat(
													item?.requestedAt,
													'YYYY-MM-DD',
													'DD MMM YYYY'
												)}
									</span>
								</p>
								<p className="regular-12 gray_dark_02 text-right">By: {item?.requestedBy}</p>
							</div>
						)}

						{(item?.status !== 'REQUESTED' || item?.status !== 'APPROVED') &&
							activeTab === 'APPROVAL' &&
							item?.approvalType !== APPROVAL_TYPE_ETD &&
							item?.approvalType !== APPROVAL_TYPE_FACTORY_PRICE &&
							(item?.requestType !== 'PRODUCT' || item?.requestType !== 'ORDER') && (
								<div className="order-cancel-status">
									<p className={`order-cancel-${item?.requestType} order-cancel`}>
										{item?.requestType === 'PRODUCT' ? 'Style' : 'Order'}{' '}
										{item?.status === 'APPROVED' ? 'canceled' : 'cancel'}{' '}
										<span className="devider">|</span>{' '}
										<span className="weight-500 text-uppercase">
											{changeDateFormat(item?.requestedAt, 'YYYY-MM-DD', 'DD MMM YYYY')}
										</span>
									</p>
									<p className="regular-12 gray_dark_02 text-right">
										By: {item?.requestedBy}
									</p>
								</div>
							)}
						<div className="brand-and-logo d-flex justify-content-between align-items-center">
							<div>
								{item?.brandResponse ? (
									<span className="brand-common-design mt-3">
										{item?.brandResponse?.name}
									</span>
								) : (
									''
								)}
							</div>
							{source === 'SCPI' && (
								<div className="commercial-dashboard-state">
									<span className={`state-title ${item.documentType}`}>
										{capitalizeFirstLetter(item.documentType.replace('_', ' '))}
									</span>
								</div>
							)}

							{item?.status === 'PENDING' && (
								<div className="order-pending-state">
									<div className="state-details d-flex align-items-center">
										<div className="state-content ml-1">
											{item?.workflowResponseList?.map(
												(item, index) =>
													item?.memberResponseList && (
														<React.Fragment
															key={`${index}__workflowResponseList`}
														>
															{item?.memberResponseList[0]?.profilePicDocument
																?.docUrl ? (
																<img
																	src={
																		item?.memberResponseList[0]
																			?.profilePicDocument?.docUrl
																	}
																	alt=""
																/>
															) : (
																<img
																	src={`${process.env.PUBLIC_URL}/images/pro_pic_default.svg`}
																	alt=""
																/>
															)}

															<span className={`state-title ${item.stepName}`}>
																{item.stepName}
															</span>
														</React.Fragment>
													)
											)}
											<div className="four-states">
												{item?.workflowResponseList?.map((step) => (
													<span
														key={step.workflowStep}
														className={`${
															step.status === 'COMPLETED'
																? 'complete-state'
																: step.status === 'PENDING' &&
																	  step?.memberResponseList
																	? 'pending-state'
																	: ''
														}`}
													/>
												))}
											</div>
										</div>
									</div>
									{isStartFromHere && index === 0 && (
										<span className="custom-tooltip">
											<span className="tooltiptext">Start form here</span>
										</span>
									)}
								</div>
							)}
							{(item?.status === 'ON_SHIPMENT' || item?.status === 'COMPLETED') && (
								<div className="order-pending-state">
									<div className="state-details d-flex align-items-center">
										<div className="state-content pt-1 ml-1">
											{item?.status === 'ON_SHIPMENT' && (
												<>
													{item?.commercialWorkFlow?.memberResponseList[0]
														?.profilePicDocument?.docUrl ? (
														<img
															src={
																item?.commercialWorkFlow
																	?.memberResponseList[0]
																	?.profilePicDocument?.docUrl
															}
															alt=""
															className="ci-person"
														/>
													) : (
														''
													)}
													{item?.status === 'ON_SHIPMENT' && (
														<span
															className={`state-title ${item?.commercialWorkFlow?.stepName}`}
														>
															{item?.commercialWorkFlow?.stepName}
														</span>
													)}
												</>
											)}
										</div>
									</div>
								</div>
							)}
						</div>
					</div>
				</a>
			);
		});
	};

	return <div className="order-card-items">{renderOrderList()}</div>;
};

export default OrderLists;
